import * as React from "react";
import { useState, useEffect } from "react";
import * as cn from "classnames/bind";
import * as styles from "./controls-navigation.css";
import { actionCreators as uiActionCreators } from "ui/store";
import { actionCreators } from "page/calendar/store";
import {
    NavigationComponents,
    NavigationComponent,
    LocalStorageCheckoutItem,
    LOCAL_STORAGE_CHECKOUT,
} from "ui/constants";
import { withRouter, RouteComponentProps } from "react-router";
import {
    CalendarState,
    CheckoutCalendarState,
    CustomImage,
    ImageDetails,
    RubberBandItem,
    SpiralItem,
} from "burde";
import { saveCalendar } from "calendar/service";
import * as moment from "moment";
import { useResources } from "contexts/resource-context";
import ConfigurationSummary from "./configuration-summary/configuration-summary";
import { getComponentRoute } from "page/calendar/helper";
import { useConfiguration } from "contexts/configuration-context";
import * as api from "util/api";

const cx = cn.bind(styles);

const navigationCn = () =>
    cx({
        navigation: true,
    });

const navigationButtonCn = (isDisabled: boolean) =>
    cx({
        navigationButton: true,
        navigationButtonDisabled: isDisabled,
    });

const navigationButtonCheckoutCn = () =>
    cx({
        navigationButtonCheckout: true,
    });

const navigationButtonCheckoutAnimationCn = () =>
    cx({
        navigationButtonCheckoutAnimation: true,
    });

const navigationCurrentCn = () =>
    cx({
        navigationCurrent: true,
    });

const navigationTextContainerCn = () =>
    cx({
        navigationTextContainer: true,
    });

const mainNavCn = (visible: boolean) =>
    cx({
        mainNav: true,
        mainNavVisible: visible,
    });

const mainNavInnerCn = () =>
    cx({
        mainNavInner: true,
    });

const mainNavControlsCn = (visible: boolean) =>
    cx({
        mainNavControls: true,
        mainNavControlsVisible: visible,
    });

const controlsButtonCn = () =>
    cx({
        controlsButton: true,
    });

const controlsButtonCheckoutCn = () =>
    cx({
        controlsButton: true,
        controlsButtonCheckout: true,
    });

type ControlsNavigationProps = {
    NavControls_Close: string;
    NavControls_Checkout: string;
    NavControls_Summary: string;
    NavControls_ToCheckout: string;
    NavControls_Lined: string;
    CurrentText: string;
    CounterText: string;
    clickNext: () => void;
    clickPrev: () => void;
    calendar: CalendarState;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    spiral: SpiralItem | null;
    rubberband: RubberBandItem | null;
    showMainMenu: boolean;
    activeComponent: NavigationComponent;
    thumbnailBlob: Blob | null;
} & typeof uiActionCreators &
    typeof actionCreators &
    RouteComponentProps;

const ControlsNavigation: React.FC<ControlsNavigationProps> = (props) => {
    const [checkoutUrl, setCheckoutUrl] = useState<string>("");

    const resources = useResources();

    const {
        NavControls_Close,
        NavControls_Checkout,
        NavControls_Summary,
        calendar,
    } = props;

    useEffect(() => {
        document.body.classList.remove("checkout-loading");
    }, [props.location]);

    useEffect(() => {
        const getCheckoutUrl = async () => {
            const response = await api.json.get(
                `checkout/urls/${calendar.culture || "sv-SE"}`,
            );

            setCheckoutUrl(response.data.url);
        };

        getCheckoutUrl();
    }, [calendar.culture]);

    const { displayPrice = "" } = useConfiguration();

    const hasActiveComponentAndNavConfig = () => {
        return props.activeComponent && NavigationComponents;
    };

    const isFirstPage = hasActiveComponentAndNavConfig()
        ? props.activeComponent.name === NavigationComponents[0].name
        : false;
    const isLastPage = hasActiveComponentAndNavConfig()
        ? props.activeComponent.name ===
          NavigationComponents[NavigationComponents.length - 1].name
        : false;

    const stmCheckoutFormRef = React.createRef<HTMLFormElement>();
    const stmCheckoutDataRef = React.createRef<HTMLInputElement>();

    const goToLast = () => {
        let navComponent =
            NavigationComponents[NavigationComponents.length - 1];

        const targetComponentRoute = getComponentRoute(
            navComponent.route,
            calendar.calendarSize,
            calendar.calendarType,
        );

        const isOnLastPage = props.location.pathname === targetComponentRoute;

        props.UpdateShowMainMenu(false);

        if (isOnLastPage) {
            //If we already are on the last page, go to the checkout instead
            goToCheckout();
        } else {
            props.UpdateActiveNavigationComponent(navComponent);
            const targetComponentRoute = getComponentRoute(
                navComponent.route,
                calendar.calendarSize,
                calendar.calendarType,
            );
            props.history.push(targetComponentRoute);
        }
    };

    const goToCheckout = async () => {
        const {
            calendar,
            coverImage,
            backCoverImage,
            customCoverImage,
            customBackImage,
            spiral,
            rubberband,
        } = props;

        const printInfo = await saveCalendar(
            calendar,
            0,
            coverImage,
            backCoverImage,
            customCoverImage,
            customBackImage,
            spiral,
            rubberband,
        );

        const item: LocalStorageCheckoutItem = {
            calendar: calendar,
            timestamp: moment.utc(),
        };

        localStorage.setItem(LOCAL_STORAGE_CHECKOUT, JSON.stringify(item));

        const checkoutCalendarData: CheckoutCalendarState = {
            stm_reference: printInfo ? printInfo.calendarId : null,
            name: calendar.name,
            weekModules: calendar.weekModules,
            dayModules: calendar.dayModules,
            startDate: calendar.startDate,
            personalDateItems: calendar.personalDateItems,
            optionalDaySettings: calendar.optionalDaySettings,
            thumbnailId: calendar.thumbnailId,
            culture: calendar.culture,
            size: calendar.calendarSize,
            layoutId: calendar.layoutId,
            userId: calendar.userId,
            lines: calendar.lines,
            borderColor: calendar.borderColor,
            borderImageId: calendar.borderImageId,
            extraPages: calendar.extraPages,
            monthDivider: calendar.monthDivider,
            ownerPage: calendar.ownerPage,
            isTheme: calendar.isTheme,
            type: calendar.calendarType,
            customNumberOfEndPages: calendar.customNumberOfEndPages,
            coverImageId: calendar.coverImageId,
            customCoverImageId: calendar.customCoverImageId,
            coverColor: calendar.coverColor,
            backCoverImageId: calendar.backCoverImageId,
            customBackImageId: calendar.customBackImageId,
            backCoverColor: calendar.backCoverColor,
            spiral: calendar.spiral,
            rubberband: calendar.rubberband,
            coverTexts: calendar.coverTexts,
            fontSetName: calendar.fontSetName,
            fontId: calendar.fontId,
        };

        const stmCheckoutForm = stmCheckoutFormRef.current;
        const stmCheckoutData = stmCheckoutDataRef.current;

        if (stmCheckoutForm && stmCheckoutData) {
            stmCheckoutData.value = JSON.stringify({
                options: checkoutCalendarData,
            });

            document.body.classList.add("checkout-loading");
            stmCheckoutForm.submit();
        }
    };

    return (
        <>
            <div className={navigationCn()}>
                <div
                    className={navigationButtonCn(isFirstPage)}
                    onClick={() => props.clickPrev()}>
                    <i
                        style={{ color: isFirstPage ? "#b7b7b7" : "" }}
                        className={"fa fa-chevron-left"}
                    />
                </div>
                {!isLastPage && (
                    <>
                        <div
                            className={navigationCurrentCn()}
                            onClick={() => props.UpdateShowMainMenu(true)}>
                            <div className={navigationTextContainerCn()}>
                                <span>{resources[props.CurrentText]}</span>
                                {props.CounterText}
                            </div>
                        </div>

                        <div
                            className={navigationButtonCn(isLastPage)}
                            onClick={() => props.clickNext()}>
                            <i
                                style={{ color: isLastPage ? "#b7b7b7" : "" }}
                                className={"fa fa-chevron-right"}
                            />
                        </div>
                    </>
                )}
                {isLastPage && (
                    <div
                        className={navigationButtonCheckoutCn()}
                        onClick={() => goToLast()}>
                        {`${resources.NavControls_ToCheckout}, ${displayPrice}`}
                        <i
                            className={`fa fa-chevron-right ${navigationButtonCheckoutAnimationCn()}`}
                        />
                    </div>
                )}
            </div>
            <form method="post" action={checkoutUrl} ref={stmCheckoutFormRef}>
                <input
                    type="hidden"
                    name="data"
                    ref={stmCheckoutDataRef}></input>
            </form>
            <div id={"navigationWrapper"}>
                <div className={mainNavCn(props.showMainMenu)}>
                    <div className={mainNavInnerCn()}>
                        <ConfigurationSummary />
                    </div>
                </div>

                {/* Main Navigation controls */}
                <div className={mainNavControlsCn(props.showMainMenu)}>
                    <button
                        className={controlsButtonCn()}
                        onClick={() => props.UpdateShowMainMenu(false)}>
                        <i className={"fa fa-times"} /> {NavControls_Close}
                    </button>
                    <button
                        className={controlsButtonCheckoutCn()}
                        onClick={() => {
                            goToLast();
                        }}>
                        <i className={"fa fa-arrow-right"} />{" "}
                        {isLastPage
                            ? NavControls_Checkout
                            : NavControls_Summary}
                    </button>
                </div>
            </div>
        </>
    );
};

export default withRouter(ControlsNavigation);
